import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { stateFromHTML } from "draft-js-import-html";
import { downloadApiExport, getDateForFileName, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus, TParamsPagination } from "./types";

// Get all sales
export const fetchTransferTermSales = createAsyncThunk(
    "transferTermSales/fetchTransferTermSales",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term-sales`, {
                params: options,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get sales of one term
export const fetchTransferTermSale = createAsyncThunk(
    "transferTermSales/fetchTransferTermSale",
    async ({ saleId }: { saleId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term-sales/${saleId}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Update a term sale
export const updateTransferTermSale = createAsyncThunk(
    "transferTermSales/updateTransferTermSale",
    async (
        {
            saleId,
            data,
            applyToGroup = false,
        }: {
            saleId: number;
            data: Pick<ITransferTermSale, "grossAmount" | "netAmount" | "statementUrl" | "status">;
            applyToGroup?: boolean;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term-sales/${saleId}`,
                data,
                { params: { applyToGroup } },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Delete a term sale
export const deleteTransferTermSale = createAsyncThunk(
    "transferTermSales/deleteTransferTermSale",
    async (
        {
            saleId,
        }: {
            saleId: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term-sales/${saleId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Export all transferTerms
export const downloadTransferTermSales = createAsyncThunk(
    "transferTermSales/downloadTransferTerms",
    async (_, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term-sales/export`, {
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `export_transferRoyalties_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Transfer rights to Management
export const convertTransferTermSale = createAsyncThunk(
    "transferTermSales/convertTransferTermSale",
    async (
        {
            saleId,
            channelId,
            applyToGroup = false,
        }: {
            saleId: number;
            channelId: number;
            applyToGroup?: boolean;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term-sales/${saleId}/convert`,
                null,
                { params: { channelId, applyToGroup } },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Add transferee sales in bulk
export const addTransfereeRights = createAsyncThunk(
    "transferTermSales/addTransfereeRights",
    async ({ data, transfereeId }: { data: any; transfereeId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transferee/${transfereeId}/sales`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Create an invoice from a sale (when client uses Bexio)
export const createSaleInvoice = createAsyncThunk(
    "transferTermSales/createSaleInvoice",
    async ({ contactId, salesId }: { contactId: number; salesId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term-sales/${salesId}/invoice`,
                { contactId },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// I haven't added the create action because the client isn't supposed to create a sale. That is done on the backend side automatically.

export enum ETransferTermSalesStatus {
    DUE_LATER = 0,
    DUE_NEXT = 1,
    WAITING_STATEMENT = 2,
    LATE_STATEMENT = 3,
    INVOICE_TO_ISSUE = 4,
    WAITING_PAYMENT = 5,
    PAYED = 6,
    LATE_PAYMENT = 7,
}

export interface ITransferTermSale {
    transferTermId: number;
    transfereeName: string;
    workName?: string;
    titleName: string;
    transfereeId: number;
    workId?: number | null;
    titleId: number | null;
    companyId: number;
    name: string;
    netAmount: number;
    grossAmount: number;
    status: ETransferTermSalesStatus;
    invoiceUrl: string;
    statementUrl: string;
    statementReminder: string | number;
    paymentDate: Date | number | null;
    metadata: any;
    id: number;
    createdAt: string;
    modifiedAt: string;
    dueDate: string;
    isArchived: number;
    type: "advance" | "sales";
    isDeleted: number;
    salesId: number | null;
    termreference: string | null;
}

interface InitialState {
    transferTermSale: ITransferTermSale;
    transferTermSales: ITransferTermSale[];
    transferTermSalesStatus: TFetchStatus;
    salesCount: number;
    countStatus0: number;
    countStatus1: number;
    countStatus2: number;
    countStatus3: number;
    countStatus4: number;
    countStatus5: number;
    countStatus6: number;
    countStatus7: number;
}

const initialState: InitialState = {
    transferTermSale: {} as ITransferTermSale,
    transferTermSales: [],
    transferTermSalesStatus: "idle",
    salesCount: 0,
    countStatus0: 0,
    countStatus1: 0,
    countStatus2: 0,
    countStatus3: 0,
    countStatus4: 0,
    countStatus5: 0,
    countStatus6: 0,
    countStatus7: 0,
};

const transferTermSalesSlice = createSlice({
    name: "transferTermSales",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchTransferTermSales.fulfilled, (state, { payload }) => {
            state.transferTermSales = payload.entities;
            state.salesCount = payload.count;
            // SEE ENUM -> ETransferTermSalesStatus
            state.countStatus0 = payload.countStatus0;
            state.countStatus1 = payload.countStatus1;
            state.countStatus2 = payload.countStatus2;
            state.countStatus3 = payload.countStatus3;
            state.countStatus4 = payload.countStatus4;
            state.countStatus5 = payload.countStatus5;
            state.countStatus6 = payload.countStatus6;
            state.countStatus7 = payload.countStatus7;
            state.transferTermSalesStatus = "succeeded";
        });
        builder.addCase(fetchTransferTermSales.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.transferTermSalesStatus = "failed";
        });
        builder.addCase(fetchTransferTermSale.fulfilled, (state, { payload }) => {
            state.transferTermSale = payload;
        });
        builder.addCase(fetchTransferTermSale.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(updateTransferTermSale.fulfilled, (state, { payload }) => {
            state.transferTermSales = state.transferTermSales.map((sale) => {
                if (sale.id === payload.id) {
                    return { ...sale, ...payload };
                } else {
                    return sale;
                }
            });
            toast.success(`${t("TransferTerms.Store.PaymentUpdateSuccess")}`);
        });
        builder.addCase(updateTransferTermSale.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteTransferTermSale.fulfilled, (state, action) => {
            state.transferTermSales = state.transferTermSales.filter((sale) => sale.id !== action.meta.arg.saleId);
            toast.success(`${t("TransferTerms.Store.PaymentDeleteSuccess")}`);
        });
        builder.addCase(deleteTransferTermSale.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(convertTransferTermSale.fulfilled, (state, { payload }) => {
            const updatedSales = [...state.transferTermSales].map((item) => {
                if (item.id === payload.id) {
                    return { ...item, salesId: payload.salesId };
                } else {
                    return item;
                }
            });

            state.transferTermSales = updatedSales;
            toast.success(`${t("TransferTerms.Store.TransferSuccess")}`);
        });
        builder.addCase(convertTransferTermSale.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(addTransfereeRights.fulfilled, (state, { payload }) => {
            state.transferTermSales = [...state.transferTermSales].map((sale) => {
                const saleModified = payload.find((item) => item.id === sale.id);
                if (saleModified) {
                    return {
                        ...sale,
                        status: 4,
                        grossAmount: saleModified.grossAmount,
                        netAmount: saleModified.netAmount,
                    };
                } else {
                    return sale;
                }
            });
            toast.success(`${t("TransferTerms.Store.AddRightsSuccess")}`);
        });
        builder.addCase(addTransfereeRights.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(createSaleInvoice.fulfilled, (state, action) => {
            state.transferTermSales = [...state.transferTermSales].map((sale) => {
                if (sale.id === action.meta.arg.salesId) {
                    return {
                        ...sale,
                        invoiceUrl: action.payload.invoiceUrl,
                        status: ETransferTermSalesStatus.WAITING_PAYMENT,
                    };
                } else {
                    return sale;
                }
            });
            toast.success(`${t("TransferTerms.Store.AddInvoiceSuccess")}`);
        });
        builder.addCase(createSaleInvoice.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default transferTermSalesSlice.reducer;
